import axios from "axios";
import config from "@/lib/config";
import { attachApiHeaders } from "@/lib/attachApiHeaders";

const API_URL = `${config.apiUrl}/purchases`;

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use(attachApiHeaders);

/**
 * @description Sends an array of processed SRI purchase data to the bulk import endpoint.
 * Endpoint: POST /purchases/import/sri
 * @param {Array<object>} purchaseArray - Array of purchase objects ready for bulk importation.
 * @returns {Promise<object>} Promise that resolves to an object with import statistics ({ importedCount, skippedCount }).
 */
export const importPurchases = async (purchaseArray) => {
  try {
    const res = await api.post("/import/sri", purchaseArray);
    return res.data;
  } catch (error) {
    console.error(
      "Error importing bulk SRI purchases:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Creates a new purchase.
 * @param {object} purchaseData - Purchase data.
 */
export const createPurchase = async (purchaseData) => {
  try {
    const res = await api.post("/", purchaseData);
    return res.data;
  } catch (error) {
    console.error(
      "Error creating purchase:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * @description Retrieves all purchases for the authenticated user with pagination and filtering options.
 * @param {number} [page] The page number for pagination.
 * @param {number} [limit] The item limit per page.
 * @param {number} [month] Month to filter purchases (1-12).
 * @param {number} [year] Year to filter purchases (e.g., 2024).
 * @param {string} [category] Category to filter purchases.
 */
export const getAllPurchases = async (
  page = 1,
  limit = 10,
  month,
  year,
  category,
  provider
) => {
  try {
    const params = {};

    if (page !== undefined) params.page = page;
    if (limit !== undefined) params.limit = limit;
    if (month !== undefined) params.month = month;
    if (year !== undefined) params.year = year;
    if (category !== undefined) params.category = category;
    if (provider !== undefined) params.provider = provider;

    const res = await api.get("/", {
      params: params,
    });

    return res.data;
  } catch (error) {
    console.error(
      "Error fetching purchases:",
      error.response?.data || error.message
    );
    return [];
  }
};

/**
 * Retrieves a purchase by its ID.
 * @param {string} id - The ID of the purchase.
 */
export const getPurchaseById = async (id) => {
  try {
    const res = await api.get(`/${id}`);
    return res.data;
  } catch (error) {
    console.error(
      `Error fetching purchase with id ${id}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Updates an existing purchase.
 * @param {string} id - The ID of the purchase to update.
 * @param {object} purchaseData - The data to update.
 */
export const updatePurchase = async (id, purchaseData) => {
  try {
    const res = await api.patch(`/${id}`, purchaseData);
    return res.data;
  } catch (error) {
    console.error(
      `Error updating purchase with id ${id}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Deletes a purchase by its ID.
 * @param {string} id - The ID of the purchase to delete.
 */
export const deletePurchase = async (id) => {
  try {
    const res = await api.delete(`/${id}`);
    return res.data;
  } catch (error) {
    console.error(
      `Error deleting purchase with id ${id}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Retrieves a purchase report for a specific month and year.
 * Llama a: GET /purchases/report?month=&year=
 * @param {number} month - The month for the report (1-12).
 * @param {number} year - The year for the report (e.g., 2024).
 * @returns {Promise<object>} - The purchase report data.
 */
export const getPurchaseReport = async (year) => {
  try {
    const res = await api.get(`/report`, {
      params: { year },
    });
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching purchase report:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Retrieves a detailed purchase report for a specific year.
 * Endpoint: GET /purchases/report/detailed?year=
 * @param {number} year - The year for the detailed report (e.g., 2024).
 * @returns {Promise<object>} - The detailed purchase report data.
 */
export const getDetailedPurchaseReport = async (year) => {
  try {
    const res = await api.get("/report/detailed", {
      params: { year },
    });
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching detailed purchase report:",
      error.response?.data || error.message
    );
    throw error;
  }
}; 

/**
 * Retrieves all purchases for the authenticated user, including full details.
 * Endpoint: GET /purchases/full
 * @returns {Promise<Array<object>>} - Array of full purchase objects.
 */
export const getUserFullPurchases = async (year, month) => {
  try {
    const res = await api.get("/full", {
      params: { year, month },
    });
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching user full purchases:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Retrieves deductible info for the authenticated user for a given year.
 * Endpoint: GET /purchases/deductible-info?year=
 * @param {number} year - The year to calculate the deductible info for.
 * @returns {Promise<object>} - The deductible info object.
 */
export const getDeductibleInfo = async (year) => {
  try {
    const res = await api.get("/deductible-info", {
      params: { year },
    });
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching deductible info:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Updates the category of a purchase.
 * Endpoint: PATCH /purchases/:id/category
 * @param {string} id - The ID of the purchase to update.
 * @param {string} categoryId - The new category ID.
 * @returns {Promise<object>} - The updated purchase object.
 */
export const updatePurchaseCategory = async (id, categoryId) => {
  try {
    const res = await api.patch(`/${id}/category`, { categoryId });
    return res.data;
  } catch (error) {
    console.error(
      `Error updating category for purchase with id ${id}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Retrieves invoice details from SRI using the provided access key.
 * Endpoint: GET /purchases/invoice-details/:accessKey
 * @param {string} accessKey - The SRI access key.
 * @returns {Promise<object>} - The invoice details.
 */
export const getInvoiceDetailsFromSRI = async (accessKey) => {
  try {
    const res = await api.get(`/invoice-details/${accessKey}`);
    return res.data;
  } catch (error) {
    console.error(
      `Error fetching invoice details for accessKey ${accessKey}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};